<script setup lang="ts">
import type { ListboxContentProps } from 'reka-ui'
import type { HTMLAttributes } from 'vue'
import { ChevronDownIcon, ChevronUpIcon } from '@heroicons/vue/24/outline'
import { reactiveOmit, useMutationObserver, useResizeObserver, useScroll } from '@vueuse/core'
import { computed, ref } from 'vue'
import { cn } from '#ui-kit/lib/utils'
import { CommandList } from '#ui-kit/ui/command'

const props = withDefaults(
  defineProps<ListboxContentProps & { class?: HTMLAttributes['class']; scrollHint?: boolean }>(),
  { scrollHint: true },
)

const forwarded = reactiveOmit(props, 'class', 'scrollHint')

const listRef = ref<{ $el?: HTMLElement } | null>(null)
const scrollEl = computed(() => listRef.value?.$el ?? null)
const { arrivedState } = useScroll(scrollEl)

const hasOverflow = ref(false)

function recalc() {
  const el = scrollEl.value
  hasOverflow.value = !!el && el.scrollHeight > el.clientHeight
}

useResizeObserver(scrollEl, recalc)
useMutationObserver(scrollEl, recalc, { childList: true, subtree: true })

const canScrollUp = computed(() => hasOverflow.value && !arrivedState.top)
const canScrollDown = computed(() => hasOverflow.value && !arrivedState.bottom)
</script>

<template>
  <div class="relative">
    <CommandList
      ref="listRef"
      v-bind="forwarded"
      :class="cn('max-h-80 overflow-y-auto overflow-x-hidden p-2', props.class)"
    >
      <slot />
    </CommandList>
    <div
      v-show="scrollHint && canScrollUp"
      class="absolute left-0 right-3 top-0 flex h-8 items-start justify-center pt-1 bg-linear-to-b from-white from-50% to-transparent pointer-events-none"
    >
      <ChevronUpIcon class="size-4 opacity-50" />
    </div>
    <div
      v-show="scrollHint && canScrollDown"
      class="absolute left-0 right-3 bottom-0 flex h-8 items-end justify-center pb-1 bg-linear-to-t from-white from-50% to-transparent pointer-events-none"
    >
      <ChevronDownIcon class="size-4 opacity-50" />
    </div>
  </div>
</template>
